import * as moment from "moment";

export class DateUtilDST {

  public static convertFromUTC(date: Date): string {
    const tz = sessionStorage.getItem('userTimeZoneIANA');
    const formattedDateMilitary = moment(date).format("YYYY-MM-DD HH:mm:ss");
    const dateMinCreationTimeZoneIANA = moment.tz(formattedDateMilitary, 'YYYY-MM-DD HH:mm:ss', tz);
    return dateMinCreationTimeZoneIANA.utc().format();
  }

  public static convertFromUTCFormat(date: Date, outputFormat: string): string {
    const tz = sessionStorage.getItem('userTimeZoneIANA');
    const formattedDateMilitary = moment(date).format("YYYY-MM-DD HH:mm:ss");
    const dateMinCreationTimeZoneIANA = moment.tz(formattedDateMilitary, 'YYYY-MM-DD HH:mm:ss', tz);
    return dateMinCreationTimeZoneIANA.utc().format(outputFormat);
  }

  public static returnDateToSave(dateinput: Date) {
    const userTz = sessionStorage.getItem('userTimeZoneIANA');
    const fechaStr = moment(dateinput)
      .format("YYYY-MM-DD HH:mm:ss"); // convierte a string sin zona
    const localDate = moment.tz(fechaStr, "YYYY-MM-DD HH:mm:ss", userTz);
    return localDate.utc().format();
  }

  /**
    * Converts a Date or date string into the user's time zone representation.
    * Accepts:
    *  - Date
    *  - ISO string (e.g. 2025-11-19T13:45:00Z)
    *  - "YYYY-MM-DD HH:mm:ss" format (browser local time is assumed if no zone is present)
    * If userTimeZoneIANA is not in sessionStorage, returns the date in UTC.
   */
  public static formatInUserTimezone(dateinput: Date | string): string {
    const userTz = sessionStorage.getItem('userTimeZoneIANA');
    let m = moment(dateinput);
    if (!m.isValid() && typeof dateinput === 'string') {
      m = moment(dateinput, "YYYY-MM-DD HH:mm:ss", true);
    }
    if (!m.isValid()) {
      return "";
    }
    if (!userTz) {
      return m.utc().format("YYYY-MM-DD HH:mm:ss");
    }

    return m.clone().tz(userTz).format("YYYY-MM-DD HH:mm:ss");
  }

  public static getCurrentDateWithSpecificTimeUTC(hour: any, minute: any): string {
    const tz = sessionStorage.getItem('userTimeZoneIANA');
    const now = new Date();
    let currentDateWithSpecificTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
    const formattedDateMilitary = moment(currentDateWithSpecificTime).format("YYYY-MM-DD HH:mm:ss");
    const dateMinCreationTimeZoneIANA = moment.tz(formattedDateMilitary, 'YYYY-MM-DD HH:mm:ss', tz);
    return dateMinCreationTimeZoneIANA.utc().format("HH:mm");
  }

  public static formatDateShortWithTimeUTC(date: Date): string {
    return this.convertFromUTCFormat(date, "YYYY-MM-DD[T]HH:mm:ss");
  }

  public static formatTimeWithTimeUTC(date: Date): string {
    return this.convertFromUTCFormat(date, "HH:mm:ss");
  }

  public static normalizeUtcToTimeZone(date: Date | string): string {
    const tz = sessionStorage.getItem('userTimeZoneIANA');
    let localDate;
    if (typeof date === 'string') {
      const utcDate = moment.parseZone(date).utc();
      localDate = utcDate.tz(tz);
    }
    else {
      localDate = moment.utc(date).tz(tz);
    }
    return localDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  public static normalizeUtcToTimeZoneDate(date: Date | string): Date {  
     return new Date(this.normalizeUtcToTimeZone(date));
  }

  public static getHour(date: Date): string {
    return String(date.getHours()).padStart(2, "0");
  }

  public static getMinutes(date: Date): string {
    return String(date.getMinutes()).padStart(2, "0");
  }

  public static setHour(date: Date, hour: number): Date {
    date.setHours(hour);
    return date;
  }

  public static setMinutes(date: Date, hour: number): Date {
    date.setMinutes(hour);
    return date;
  }
  
  //set date in format yyyy-MM-dd
  public static setDate(date: Date, newDate: String): Date {
    date.setFullYear(Number(newDate.substring(0, 4)));
    date.setMonth(Number(newDate.substring(5, 7)) - 1);
    date.setDate(Number(newDate.substring(8, 10)));
    return date;
  }

  /**
   * Verifies if Daylight Saving Time (DST) is currently active in the user's configured timezone.
   * @returns true if DST is active in the current date and user's timezone, false otherwise.
   * Returns false if no timezone is configured.
   */
  public static isDSTActive(): boolean {
    const userTz = sessionStorage.getItem('userTimeZoneIANA');
    if (!userTz) {
      return false;
    }
    const now = moment().tz(userTz);
    return now.isDST();
  }

  /**
   * Gets the current UTC offset for the specified timezone, taking into account Daylight Saving Time (DST).
   * @param timezoneIANA - IANA timezone identifier (e.g., 'America/New_York')
   * @returns The current offset in format "+HH:mm" or "-HH:mm" (e.g., "+05:00", "-03:00")
   * Returns "+00:00" if timezone is not provided.
   */
  public static getCurrentOffset(timezoneIANA: string): string {
    if (!timezoneIANA) {
      return '+00:00';
    }
    const now = moment().tz(timezoneIANA);
    const offsetMinutes = now.utcOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  /**
   * Checks if a given date is in the past compared to the current time in the user's timezone.
   * This method properly handles timezone differences and Daylight Saving Time (DST).
   * @param date - The date to check (can be a Date object or date string)
   * @returns true if the date is in the past, false otherwise
   */
  public static isDateInPast(date: Date | string): boolean {
    const userTz = sessionStorage.getItem('userTimeZoneIANA');
    
    if (!userTz) {
      // Fallback comparison without timezone
      const dateToCompare = date instanceof Date ? date : new Date(date);
      return dateToCompare < new Date();
    }
    
    // Format the date and get current time in user's timezone
    const dateStr = moment(date).format('YYYY-MM-DD HH:mm:ss');
    const selectedMoment = moment.tz(dateStr, 'YYYY-MM-DD HH:mm:ss', userTz);
    const nowMoment = moment().tz(userTz);
    
    // Compare dates (this automatically handles DST)
    return selectedMoment.isBefore(nowMoment);
  }

  /**
   * Converts a UTC time string (HH:mm format) to the user's configured timezone.
   * This method properly handles Daylight Saving Time (DST) transitions.
   * @param timeUtc - Time in UTC format "HH:mm" (e.g., "14:30")
   * @returns Object with hours and minutes in the user's timezone, both as padded strings
   */
  public static convertUTCTimeToUserTimezone(timeUtc: string): { hours: string, minutes: string } {
    const userTz = sessionStorage.getItem('userTimeZoneIANA');
    
    if (!userTz || !timeUtc) {
      // Fallback: return the original time if no timezone is configured
      const [hours, minutes] = timeUtc ? timeUtc.split(':') : ['00', '00'];
      return { 
        hours: hours.padStart(2, '0'), 
        minutes: minutes.padStart(2, '0') 
      };
    }
    
    // Parse the UTC time (HH:mm) and create a moment object in UTC
    const [hours, minutes] = timeUtc.split(':');
    const utcTime = moment.utc().set({ 
      hour: parseInt(hours, 10), 
      minute: parseInt(minutes, 10), 
      second: 0, 
      millisecond: 0 
    });
    
    // Convert to user's timezone
    const localTime = utcTime.tz(userTz);
    
    return {
      hours: localTime.format('HH'),
      minutes: localTime.format('mm')
    };
  }
}